import store from '@/store'
import { Action, config, getModule, Module, VuexModule } from "vuex-module-decorators";
import Viewpoints from "@/store/modules/Viewpoints";
import Decisions from "@/store/modules/Decisions";
import Modelling from "@/store/modules/Modelling";
import { Factor, FactorGroup, FactorResource } from "@/api/model/FactorResource";
import { ViewpointFactorResource } from "@/api/model/ViewpointFactorResource";

config.rawError = true

const name = 'ScoringKeys';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

const viewpoints = getModule(Viewpoints);
const decisions = getModule(Decisions);
const modelling = getModule(Modelling);

const DEFAULT_SCORING_KEY_DEFINITION: ScoringKeyDefinition = {
    notApplicableEnabled: false, scoringKeys: []
}

@Module({dynamic: true, store, name: name, namespaced: true})
export default class ScoringKeys extends VuexModule {

    get workingModelScoringKeyDefinitions(): FactorResourcesScoringKeyDefinitions {
        let factorResources = modelling.workingModelFactorResources;
        if (factorResources) {
            return ScoringKeys.generateViewpointScoringKeyDefinition(factorResources, {}) || {};
        }
        return {};
    }

    get viewpointScoringKeyDefinitions(): { [viewpointId: string]: FactorResourcesScoringKeyDefinitions } {
        let viewpointsSelected = viewpoints.selectedViewpointIds;
        let factorResources = modelling.publishedModelFactorResources;


        let viewpointScoringKeyDefinitions: { [viewpointId: string]: FactorResourcesScoringKeyDefinitions } = {}
        for (let viewpointId of viewpointsSelected) {
            let viewpointFactorResources = viewpoints.viewpointFactorResources[viewpointId] || {};

            if (factorResources) {
                viewpointScoringKeyDefinitions[viewpointId] = ScoringKeys.generateViewpointScoringKeyDefinition(factorResources, viewpointFactorResources) || {};
            }
        }
        console.log("viewpointScoringKeyDefinitions",viewpointScoringKeyDefinitions)
        return viewpointScoringKeyDefinitions;
    }

    @Action
    async updateViewpointScoringKeyDefinition(payload: { viewpointId: string, scoringKeyDefinition: ScoringKeyDefinition | undefined }) {
        let updatePayload = {
            viewpointId: payload.viewpointId,
            factorResourceId: 'M',
            scoringKeyDefinition: payload.scoringKeyDefinition
        }
        return await this.updateViewpointFactorResourceScoringKeyDefinition(updatePayload);
    }

    @Action
    async updateViewpointFactorResourceScoringKeyDefinition(payload: { viewpointId: string, factorResourceId: string, scoringKeyDefinition: ScoringKeyDefinition | undefined }) {
        let viewpointFactorResource = viewpoints.viewpointFactorResources[payload.viewpointId][payload.factorResourceId];
        if (!viewpointFactorResource) {
            viewpointFactorResource = {
                viewpointId: payload.viewpointId,
                id: payload.factorResourceId,
                scoringKeyDefinition: payload.scoringKeyDefinition
            }
        }
        viewpointFactorResource.scoringKeyDefinition = payload.scoringKeyDefinition;
        return await viewpoints.putViewpointFactor(viewpointFactorResource);
    }

    @Action
    async updateDecisionScoringKeyDefinition(scoringKeyDefinition: ScoringKeyDefinition | null) {
        let rootModelFactorResource = modelling.workingModelFactorResources['M'] as FactorGroup;
        if (!rootModelFactorResource) return;
        rootModelFactorResource.scoringKeyDefinition = scoringKeyDefinition;
        return await modelling.updateFactorResource(rootModelFactorResource)
    }

    private static generateViewpointScoringKeyDefinition(factorResources: { [id: string]: FactorResource }, viewpointFactorResources: { [id: string]: ViewpointFactorResource }) {
        return ScoringKeys.convertFactorResource(factorResources, viewpointFactorResources, 'M', undefined, {})
    }

    // Takes a tree element and converts it into a scoring key definition and adds it to the resourcesScoringKeyDefinitions map.
    // That map is then returned.
    private static convertFactorResource(factorResources: { [id: string]: FactorResource }, viewpointFactorResources: any,
                                         resourceId: string, inheritedScoringKeyDefinition: ScoringKeyDefinition | undefined,
                                         resourcesScoringKeyDefinitions: { [resourceId: string]: ScoringKeyDefinition | undefined }) {
        let factorResource = factorResources[resourceId];
        let viewpointFactorResource = viewpointFactorResources[resourceId]
        if (!factorResource) {
            if (resourceId !== 'M') {
                console.warn("There is an orphan factor in this model: " + resourceId)
            }
            return resourcesScoringKeyDefinitions
        }

        if (factorResource.type === 'FACTOR') {
            let factor = factorResource as Factor
            let resourceScoringKeyDefinition = inheritedScoringKeyDefinition;
            if (viewpointFactorResource && viewpointFactorResource.scoringDefinition && viewpointFactorResource.scoringDefinition.scoringRules
                && viewpointFactorResource.scoringDefinition.scoringRules.length > 0) {
                resourceScoringKeyDefinition = {
                    inheritedFrom: "VIEWPOINT",
                    notApplicableEnabled: viewpointFactorResource.scoringDefinition.notApplicableEnabled,
                    scoringKeys: viewpointFactorResource.scoringDefinition.scoringRules.map((sr: any) => sr.scoringKey)
                }
            } else if (factor && factor.scoringDefinition && factor.scoringDefinition.scoringRules
                && factor.scoringDefinition.scoringRules.length > 0) {
                resourceScoringKeyDefinition = {
                    inheritedFrom: "DECISION",
                    notApplicableEnabled: factor.scoringDefinition.notApplicableEnabled,
                    scoringKeys: factor.scoringDefinition.scoringRules.map((sr: any) => sr.scoringKey)
                }
            }
            resourcesScoringKeyDefinitions[resourceId] = resourceScoringKeyDefinition;
        } else if (factorResource.type === 'FACTOR_GROUP') {
            let factorGroupResource = factorResource as FactorGroup;
            let resourceScoringKeyDefinition = inheritedScoringKeyDefinition;
            if (viewpointFactorResource && viewpointFactorResource.scoringKeyDefinition && viewpointFactorResource.scoringKeyDefinition.scoringKeys
                && viewpointFactorResource.scoringKeyDefinition.scoringKeys.length > 0) {
                resourceScoringKeyDefinition = {
                    inheritedFrom: "VIEWPOINT",
                    inheritedFromResourceId: resourceId,
                    notApplicableEnabled: viewpointFactorResource.scoringKeyDefinition.notApplicableEnabled,
                    scoringKeys: viewpointFactorResource.scoringKeyDefinition.scoringKeys
                }
            } else if ((!inheritedScoringKeyDefinition || inheritedScoringKeyDefinition.inheritedFrom === 'DECISION')
                && factorGroupResource && factorGroupResource.scoringKeyDefinition && factorGroupResource.scoringKeyDefinition.scoringKeys
                && factorGroupResource.scoringKeyDefinition.scoringKeys.length > 0) {
                resourceScoringKeyDefinition = {
                    inheritedFrom: "DECISION",
                    inheritedFromResourceId: resourceId,
                    notApplicableEnabled: factorGroupResource.scoringKeyDefinition.notApplicableEnabled,
                    scoringKeys: factorGroupResource.scoringKeyDefinition.scoringKeys
                }
            }
            resourcesScoringKeyDefinitions[resourceId] = resourceScoringKeyDefinition;

            (factorGroupResource.childrenIds || []).forEach(cId =>
                ScoringKeys.convertFactorResource(factorResources, viewpointFactorResources, cId,
                    resourceScoringKeyDefinition, resourcesScoringKeyDefinitions));

        }
        return resourcesScoringKeyDefinitions;
    }

}

interface ScoringKeyDefinition {
    scoringKeys: any[],
    notApplicableEnabled: boolean,
    inheritedFrom?: 'VIEWPOINT' | 'DECISION',
    inheritedFromResourceId?: string
}

interface FactorResourcesScoringKeyDefinitions {
    [resourceId: string]: ScoringKeyDefinition | undefined;
}

export {
    FactorResourcesScoringKeyDefinitions,
    ScoringKeyDefinition
}
